import {Component, HostListener, Input, OnDestroy, OnInit} from '@angular/core';
import {SocialDataProviderService} from '../social-grid-list/social-data-provider.service';
import {tap} from 'rxjs/operators';
import {SocialGridItemInterface} from '../social-grid-list/social-grid-item.interface';
import {SwiperItemInterface} from '../social-grid-list/swiper-items.interface';
import {SocialGridListComponent} from '../social-grid-list/social-grid-list.component';
import {KeyValue} from '@angular/common';
import {Router} from '@angular/router';
import {SocialListTypeEnum} from '../social-grid-list/social-list-type.enum';

@Component({
  selector: 'app-social-timeline',
  templateUrl: './social-timeline.component.html',
  styleUrls: ['./social-timeline.component.scss']
})
export class SocialTimelineComponent implements OnInit, OnDestroy {

  constructor(
    public readonly parentComponent: SocialGridListComponent,
    private readonly socialDP: SocialDataProviderService,
    private readonly router: Router,
  ) { }

  public readonly SocialListTypeEnum = SocialListTypeEnum;
  public page = 2;
  public endOfResults = false;
  public totalItems: {[key: string]: number} = {projects: 0, users: 0, events: 0, interested: 0, participate: 0, participated: 0};
  public items: SocialGridItemInterface[] = [];
  public loading = false;
  public gridFrame: number[][] = this.buildFrame(3);
  @Input() public slide: KeyValue<string, SwiperItemInterface>;
  @Input() public noResultsMsg: string;

  ngOnInit(): void {
    this.updateGridFrame();
    this.getPlaceHolders();
    if (!this.canRequestCollection()) {
      return;
    }

    this.getCollection(1);
  }

  ngOnDestroy(): void {
    this.items = [];
    this.page = 2;
    this.endOfResults = false;
  }

  requestNextPage(): void {
    if (!this.canRequestCollection()) {
      this.loading = false;
      return;
    }

    if (this.endOfResults || (this.totalItems[this.slide.key] && this.items.length >= this.totalItems[this.slide.key])) {
      this.endOfResults = true;
      return;
    }

    this.loading = true;

    this.socialDP.getCollection(
      this.parentComponent.listType,
      this.slide,
      this.parentComponent.getAuthorApiModel(),
      this.parentComponent.event,
      this.page,
      this.parentComponent.projectId
    )
      .pipe(tap(() => this.totalItems[this.slide.key] = this.socialDP.totalItems[this.slide.key]))
      .subscribe((items: SocialGridItemInterface[]) => {

        if (!items.length) {
          this.endOfResults = true;
        }

        const oldItems = this.items.filter(e => e.isPlaceholder === false);
        this.items = [...oldItems, ...items];
        if (this.totalItems[this.slide.key] && this.items.length >= this.totalItems[this.slide.key]) {
          this.endOfResults = true;
        }

        this.page++;
        this.loading = false;
    }, () => {
      this.loading = false;
    });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateGridFrame();
  }

  private updateGridFrame(): void {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const columns = width >= 1200 ? 5 : width >= 992 ? 4 : 3;
    this.gridFrame = this.buildFrame(columns);
  }

  private buildFrame(columns: number): number[][] {
    let value = 1;
    return Array.from({length: columns}, () =>
      Array.from({length: columns}, () => value++)
    );
  }

  private getPlaceHolders(): void {
    const placeholdersCount = this.getPlaceholdersCount();
    this.items = Array.from({length: placeholdersCount}, () => ({
      isPlaceholder: true,
      thumbnail: {hyperlinks: [{name: 'thumbnail', uri: ''}]}
    }));
  }

  private getPlaceholdersCount(): number {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
    return width >= 992 ? 10 : 9;
  }

  public getDefaultThumbnailByType(type: string, item: any): 'background'|'car'|'avatar'|'motorcycle' {
    switch (type) {
      case 'events': return  'background';
      case 'projects':
        return String(item?.subject?.vehicleType || '').toUpperCase().includes('MOTORCYCLE')
          ? 'motorcycle'
          : 'car';
      case 'users': return  'avatar';
      default: return 'background';
    }
  }

  private getCollection(page: number) {
    if (!this.canRequestCollection()) {
      this.loading = false;
      this.items = [];
      return;
    }

    this.loading = true;
    this.socialDP.getCollection(
      this.parentComponent.listType,
      this.slide,
      this.parentComponent.getAuthorApiModel(),
      this.parentComponent.event,
      page,
      this.parentComponent.projectId
    )
      .pipe(tap(() => this.totalItems[this.slide.key] = this.socialDP.totalItems[this.slide.key]))
      .subscribe((socialGridItems: SocialGridItemInterface[]) => {
        this.items = [];
        this.items = socialGridItems;
        if (this.totalItems[this.slide.key] && this.items.length >= this.totalItems[this.slide.key]) {
          this.endOfResults = true;
        }
        this.loading = false;
    }, () => {
      this.loading = false;
    });
  }

  navigateTo(data: SocialGridItemInterface): void {
    if (!data.previewUrl) {
      return;
    }

    this.router.navigate([data.previewUrl]);
  }

  navigateToVehicle(event: MouseEvent, vehicleThumb: {thumb: any; previewUrl?: string | null}): void {
    this.stopEvent(event);

    if (!vehicleThumb?.previewUrl) {
      return;
    }

    this.router.navigate([vehicleThumb.previewUrl]);
  }

  onTileClick(event: MouseEvent, data: SocialGridItemInterface): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.vehicle-thumbnails-grid')) {
      this.stopEvent(event);
      return;
    }

    this.navigateTo(data);
  }

  onVehicleLinkClick(event: MouseEvent, vehicleThumb: {thumb: any; previewUrl?: string | null}): void {
    event.stopPropagation();

    if (!vehicleThumb?.previewUrl) {
      event.preventDefault();
    }
  }

  stopEvent(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    (event as MouseEvent).stopImmediatePropagation?.();
  }

  public trackByItem(index: number, item: SocialGridItemInterface): string {
    return item?.previewUrl || item?.name || `placeholder:${index}`;
  }

  private canRequestCollection(): boolean {
    return !!this.slide?.key && !!this.slide?.value && this.parentComponent?.isCollectionContextReady();
  }
}
